import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ACTION_CONSTANTS from '@progressivebeef/shared/src/constants/actionConstants';
import { clearNavigation } from '@progressivebeef/shared/src/store/actions/navigation';

/**
 * NavigationHandler component
 * Listens to Redux navigation state and performs actual navigation
 * This bridges Redux sagas with React Router
 *
 * Supports:
 * - Simple navigation: navigateTo('/path')
 * - Navigation with state: navigateTo('/path', { data: 'value' })
 * - Replace mode: navigateTo('/path', null, true)
 * - Go back: navigateBack()
 * - Go forward: navigateForward()
 */
const NavigationHandler = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const navigationRequest = useSelector(
    (state) => state.navigation.pendingNavigation,
  );
  const historyAction = useSelector(
    (state) => state.navigation.pendingHistoryAction,
  );

  // Handle regular navigation (navigateTo)
  useEffect(() => {
    if (navigationRequest) {
      const { route, state, replace } = navigationRequest;

      // Perform navigation with options
      if (state || replace) {
        navigate(route, { state, replace: !!replace });
      } else {
        navigate(route);
      }

      // Clear the pending navigation after execution
      dispatch(clearNavigation());
    }
  }, [navigationRequest, navigate, dispatch]);

  // Handle history navigation (navigateBack/navigateForward)
  useEffect(() => {
    if (historyAction) {
      const { steps } = historyAction;

      // Navigate using numeric value (negative for back, positive for forward)
      navigate(steps);

      // Clear the pending history action after execution
      dispatch(clearNavigation());
    }
  }, [historyAction, navigate, dispatch]);

  return null; // This component doesn't render anything
};

export default NavigationHandler;
